import React from 'react'

import { Separator } from 'src/components/ui/separator'
import { cn } from 'src/lib/utils'

import type { DividerProps } from './antd-props'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Divider` seam. `./Divider.tsx` is the
 * dispatcher that chooses between this and `./Divider.antd`; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Ported from the `update-design` reference branch's `src/antd/Divider.tsx`
 * (its Phase 2 slice 2). Divergences from the reference are marked
 * `[development]` below; everything else is the reference verbatim.
 *
 * The EXTERNAL API is the seam's own prop list — the same nine props
 * `./Divider.antd` forwards — so the 97 files that already import `Divider`
 * from `src/antd` switch with no edit at all.
 *
 * `.ant-divider*` is NOT re-emitted, and here that costs nothing to check:
 * no stylesheet in this repo selects it (measured — zero `.ant-divider` in
 * `src/**`, tests included). So unlike Skeleton or Card there is no live
 * override to carry over, and the seam is free to render whatever the
 * reference renders.
 *
 * [development] The reference's labelled branch renders `role="separator"` on
 * a `<div>` that CONTAINS its label. That is what antd does too
 * (`.ant-divider-with-text` is the separator element and the text lives
 * inside it), so it is kept — a separator with an accessible name is the
 * intent, not an accident.
 * ------------------------------------------------------------------------- */
export function Divider({
  type = 'horizontal',
  orientation = 'center',
  orientationMargin,
  className,
  children,
  dashed,
  style,
  plain,
}: DividerProps) {
  // Vertical: a short inline rule between inline items (antd default look).
  if (type === 'vertical') {
    return (
      <Separator
        orientation="vertical"
        // `data-[orientation=vertical]:h-…`/`:w-…`, NOT a bare `h-…`/`w-…`,
        // and this is the first correction to the reference: the Separator
        // primitive sets `data-[orientation=vertical]:h-full` and
        // `data-[orientation=vertical]:w-px` itself, and an unprefixed
        // `h-[0.9em]`/`w-0` neither outranks those (the attribute selector
        // wins) nor gets merged away by `cn` (tailwind-merge keys on the
        // variant set). `h-full` on an inline-block with no sized parent
        // computes to 0, so the reference's vertical divider draws NOTHING —
        // confirmed in the browser before this line existed; and the
        // primitive's `w-px` would keep a 1px solid box beside the dashed
        // border. Matching the variant makes tailwind-merge drop the
        // primitive's rule instead.
        className={cn(
          'mx-2 inline-block bg-border align-middle data-[orientation=vertical]:h-[0.9em]',
          dashed &&
            'border-l border-dashed border-border bg-transparent data-[orientation=vertical]:w-0',
          className
        )}
        style={style}
      />
    )
  }

  // Horizontal without a label: a full-width rule with antd-like margin.
  if (!children) {
    return (
      <Separator
        orientation="horizontal"
        className={cn(
          'my-6 bg-border',
          dashed &&
            'border-t border-dashed border-border bg-transparent data-[orientation=horizontal]:h-0',
          className
        )}
        style={style}
      />
    )
  }

  // Horizontal WITH a label: line — label — line, honoring orientation.
  const lineClass = cn(
    'h-px flex-1 bg-border',
    dashed && 'h-0 border-t border-dashed border-border bg-transparent'
  )
  // On left/right orientation the label hugs that edge: the near-side rule is
  // short (orientationMargin, else 5%); the far-side rule fills the rest.
  let shortWidth = '5%'
  if (orientationMargin != null) {
    shortWidth =
      typeof orientationMargin === 'number'
        ? `${orientationMargin}px`
        : orientationMargin
  }

  return (
    <div
      role="separator"
      className={cn(
        'my-4 flex items-center gap-4 whitespace-nowrap text-sm',
        className
      )}
      style={style}
    >
      {orientation === 'left' ? (
        <span className={lineClass} style={{ flex: `0 0 ${shortWidth}` }} />
      ) : (
        <span className={lineClass} />
      )}
      <span data-slot="divider-text" className={cn(!plain && 'font-medium')}>
        {children}
      </span>
      {orientation === 'right' ? (
        <span className={lineClass} style={{ flex: `0 0 ${shortWidth}` }} />
      ) : (
        <span className={lineClass} />
      )}
    </div>
  )
}
